import { IdToken } from '@auth0/auth0-react';
import { createSlice, PayloadAction } from '@reduxjs/toolkit';

import { USER_META_STORAGE_KEY } from 'constants/general';
import { DashboardData, GetUserCalculations, UpdateUserData } from 'types/api/api-user.types';
import { Currency } from 'types/general.types';
import { User, UserStateMeta } from 'types/user.types';
import { getUserFromCookie } from 'utils/auth';
import { setItemToStore } from 'utils/storage';

export type UserSliceType = {
  user: User | null;
  userData: DashboardData | null;
  calculations: GetUserCalculations.Calculation[];
  isLoading: boolean;
};

export const initialState: UserSliceType = {
  user: null,
  userData: null,
  calculations: [],
  isLoading: false,
};

const slice = createSlice({
  name: 'user',
  initialState,
  reducers: {
    setUser: (
      state,
      { payload: { accessToken = '', idToken } }: { payload: { accessToken: string; idToken: IdToken } },
    ) => {
      state.user = getUserFromCookie(accessToken, idToken);
    },
    setUserData: (state, action: PayloadAction<DashboardData>) => {
      state.userData = {
        ...action.payload,
        user: {
          ...action.payload.user,
          isFullInfoProvided: Boolean(
            action.payload.user.city &&
              action.payload.user.country &&
              action.payload.user.streetAddress &&
              action.payload.user.postcode &&
              action.payload.user.phoneNumber &&
              action.payload.user.firstName &&
              action.payload.user.lastName,
          ),
        },
      };
    },
    setUserInfo: (state, action: PayloadAction<Omit<UpdateUserData.RequestType, 'userId'>>) => {
      if (!state.userData?.user) return;

      state.userData.user = {
        ...state.userData.user,
        ...action.payload,
        isFullInfoProvided: Boolean(
          (action.payload.city ?? state.userData.user.city) &&
            (action.payload.country ?? state.userData.user.country) &&
            (action.payload.streetAddress ?? state.userData.user.streetAddress) &&
            (action.payload.postcode ?? state.userData.user.postcode) &&
            (action.payload.phoneNumber ?? state.userData.user.phoneNumber) &&
            (action.payload.firstName ?? state.userData.user.firstName) &&
            (action.payload.lastName ?? state.userData.user.lastName),
        ),
      };
    },
    setUserWallet: (state, action: PayloadAction<Partial<UserStateMeta>>) => {
      if (!state.userData?.user) return;
      state.userData.user = {
        ...state.userData.user,
        btcWalletAddress: action.payload.btcWalletAddress!,
      };
    },
    setCalculations: (state, action: PayloadAction<GetUserCalculations.Calculation[]>) => {
      state.calculations = action.payload;
    },
    setUserCurrency: (state, action: PayloadAction<{ currency: Currency }>) => {
      state.userData = {
        ...state.userData,
        user: {
          ...state.userData?.user,
          baseCurrency: action.payload.currency,
        },
      } as DashboardData;
    },

    // updateToken(state, action: PayloadAction<{ accessToken: string }>) {
    //   if (state.user) {
    //     state.user.accessToken = action.payload.accessToken;
    //   }
    // },
    removeCredentials: state => {
      state.user = null;
      setItemToStore({ key: USER_META_STORAGE_KEY, payload: null, store: sessionStorage });
    },
  },
});

export default slice.reducer;
// selectors
export const userSelector = (state: { user: UserSliceType }) => state.user;
export const userDataSelector = (state: { user: UserSliceType }) => state.user.userData?.user;
export const userBaseCurrencySelector = (state: { user: UserSliceType }) => state.user.userData?.user.baseCurrency;
export const costsEarningsInfoSelector = (state: { user: UserSliceType }) => state.user.userData?.costsEarningsInfo;
export const pendingLifetimeInfoSelector = (state: { user: UserSliceType }) => state.user.userData?.pendingLifetimeInfo;
export const estimatedMontlyCostInfoSelector = (state: { user: UserSliceType }) =>
  state.user.userData?.estimatedMontlyCostInfo;

// actions
export const { removeCredentials, setUserData, setUserInfo, setUserWallet, setCalculations, setUser, setUserCurrency } =
  slice.actions;
